import { ColDef, ICellRendererParams, IDatasource, IGetRowsParams, RowSelectedEvent, SortModelItem } from '@ag-grid-community/core';
import { Box, Flex, Spinner } from '@chakra-ui/react';
import { useMemo } from 'react';
import { useNavigate, useRouteLoaderData } from 'react-router-dom';
import { api } from '../../lib/api';
import { LoginResponseDto, PatientListItemDto } from '../../lib/ServerApi';
import { useTranslation } from 'react-i18next';
import { LinkButton } from '@/components/ui/link-button';
import { Plus } from 'lucide-react';
import { AccessGuard } from '@/components/access-guard';
import { StatusFilter, StatusFloatingFilter } from './status-filter';
import { BranchFilter, BranchFloatingFilter } from './branch-filter';
import { capitalize } from '@/utils/capitalize';
import { ResponsiveGrid } from '@/components/ui/responsive-grid';
import { useFeatureFlag } from '@/providers/feature-flag-provider';

function LoadingCellRenderer(props: ICellRendererParams<PatientListItemDto>) {
    if (props.value !== undefined) {
        return props.value;
    }
    return (
        <Flex align="center" color="purple.400" h="full">
            <Spinner size="sm" borderWidth="2px" />
        </Flex>
    );
}

const defaultColumnDef: ColDef<PatientListItemDto> = {
    flex: 1,
    resizable: true,
    floatingFilter: true,
    sortable: true,
    filter: true,
};

function formatName(rawName: string) {
    const parts = rawName.split(',').map((part) => capitalize(part.trim().split(/\s+/).join(' ')));

    return parts.join(', ');
}

// Map status filter values to API statuses
function mapStatusFilterToStatuses(filterValue: string | null): string[] | undefined {
    if (!filterValue) {
        return undefined; // Use server default
    }

    const statusMap: Record<string, string[]> = {
        'referral intake pending admitted': ['REFERRAL', 'INTAKE', 'PENDING', 'ADMITTED'],
        referral: ['REFERRAL'],
        intake: ['INTAKE'],
        pending: ['PENDING'],
        admitted: ['ADMITTED'],
        transferred: ['TRANSFERRED'],
        discharged: ['DISCHARGED'],
        'non admit': ['NON_ADMIT'],
    };

    return statusMap[filterValue.toLowerCase()] || undefined;
}

function buildQueryParams(
    startRow: number,
    pageSize: number,
    sortModel: SortModelItem[],
    filterModel: Record<string, { filter: unknown; value: string; dateFrom: unknown }>,
) {
    const page = Math.floor(startRow / pageSize) + 1;

    const params: Record<string, unknown> = {
        page,
        pageSize,
    };

    // Handle sorting
    if (sortModel.length > 0) {
        const { colId, sort } = sortModel[0];
        params.sortField = colId;
        params.sortDirection = sort;
    }

    if (filterModel) {
        if (filterModel.name?.filter) {
            params.name = filterModel.name.filter;
        }

        // Branch filter (custom BranchFilter sends branchId)
        if (filterModel.branchName?.value) {
            params.branchId = filterModel.branchName.value;
        }

        if (filterModel.status?.value) {
            const statuses = mapStatusFilterToStatuses(filterModel.status.value);
            if (statuses) {
                params.statuses = statuses.join(',');
            }
        }

        if (filterModel.dob?.dateFrom) {
            params.dob = filterModel.dob.dateFrom;
        }

        if (filterModel.episodeNumber?.filter !== undefined) {
            params.episodeNumber = filterModel.episodeNumber.filter;
        }

        if (filterModel.admissionNumber?.filter !== undefined) {
            params.admissionNumber = filterModel.admissionNumber.filter;
        }
    }

    return params;
}

export function PatientsPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const session = useRouteLoaderData('root') as LoginResponseDto;
    const { isEnabled } = useFeatureFlag();
    const intakeDashboardEnabled = isEnabled('intake-dashboard');

    const branches = useMemo(
        () => session.scope?.branches.map((branch) => ({ id: branch.id, name: branch.name })) || [],
        [session.scope?.branches],
    );

    const datasource: IDatasource = useMemo(
        () => ({
            getRows: async (params: IGetRowsParams) => {
                const pageSize = 50;
                const queryParams = buildQueryParams(params.startRow, pageSize, params.sortModel, params.filterModel);

                try {
                    const { data } = await api.patient.get(queryParams);

                    const rowsThisBlock = data.data || [];
                    const lastRow = data.meta.totalRecords <= params.endRow ? data.meta.totalRecords : -1;

                    params.successCallback(rowsThisBlock, lastRow);
                } catch {
                    params.failCallback();
                }
            },
        }),
        [],
    );

    const colDefs = useMemo<ColDef<PatientListItemDto>[]>(() => {
        return [
            {
                field: 'branchName',
                headerName: 'Branch',
                filter: BranchFilter,
                filterParams: { branches },
                floatingFilterComponent: BranchFloatingFilter,
                cellRenderer: LoadingCellRenderer,
            },
            {
                field: 'name',
                headerName: 'Name',
                filter: 'agTextColumnFilter',
                valueGetter: (params) => {
                    const rawName = params.data?.name || '';
                    return formatName(rawName);
                },
            },
            {
                field: 'primaryPhone',
                headerName: 'Contact',
                filter: 'agTextColumnFilter',
                maxWidth: 150,
                sortable: false,
                valueGetter: (params) => params.data?.primaryPhone || (params.node?.id ? '-' : ''),
            },
            {
                field: 'dob',
                headerName: 'Date of Birth',
                filter: 'agDateColumnFilter',
            },
            {
                colId: 'status',
                field: 'status',
                headerName: 'Status',
                filter: StatusFilter,
                filterParams: { intakeDashboardEnabled },
                floatingFilterComponent: StatusFloatingFilter,
                valueGetter: (params) => {
                    return params.data ? t(`PATIENT.STATUS.${params.data.status}`) : '';
                },
            },
            {
                field: 'episodeNumber',
                headerName: 'Episode',
                filter: 'agNumberColumnFilter',
                maxWidth: 100,
            },
            {
                field: 'admissionNumber',
                headerName: 'Admit #',
                filter: 'agNumberColumnFilter',
                maxWidth: 110,
            },
            {
                field: 'primaryInsuranceName',
                headerName: 'Insurance',
                filter: 'agTextColumnFilter',
                sortable: false,
                valueGetter: (params) => params.data?.primaryInsuranceName || (params.node?.id ? '-' : ''),
            },
            {
                field: 'primaryPhysician',
                headerName: 'Provider',
                filter: 'agTextColumnFilter',
                sortable: false,
                valueGetter: (params) => params.data?.primaryPhysician || (params.node?.id ? '-' : ''),
            },
        ];
    }, [t, branches, intakeDashboardEnabled]);

    const handleSelect = (event: RowSelectedEvent<PatientListItemDto>) => {
        if (event.node.isSelected()) {
            if (event.data?.status === 'INTAKE' || event.data?.status === 'REFERRAL' || event.data?.status === 'NON_ADMIT') {
                navigate(`/branch/${event.data.branchId}/admission/${event.data.admissionId}/intake`);
            } else {
                navigate(`/branch/${event.data?.branchId}/admission/${event.data?.admissionId}/patient`);
            }
        }
    };

    return (
        <Flex px={{ base: '16px', md: 0 }} pt="16px" h="100%" flexDirection="column">
            <Flex alignItems="center" justifyContent="space-between" pb="12px" px={{ base: 0, md: '16px' }} gap="8px">
                <Box />
                <AccessGuard permission="intake:create">
                    <Flex>
                        <LinkButton size="xs" to="/intake" textDecoration="none">
                            <Plus /> New Patient
                        </LinkButton>
                    </Flex>
                </AccessGuard>
            </Flex>
            <Box position="relative" w="100%" h="100%" pb="24px">
                <ResponsiveGrid<PatientListItemDto>
                    rowModelType="infinite"
                    datasource={datasource}
                    cacheBlockSize={50}
                    maxBlocksInCache={10}
                    rowBuffer={0}
                    infiniteInitialRowCount={1000}
                    maxConcurrentDatasourceRequests={1}
                    rowSelection="single"
                    columnDefs={colDefs}
                    suppressCellFocus
                    defaultColDef={defaultColumnDef}
                    autoSizeStrategy={{ type: 'fitGridWidth' }}
                    onRowSelected={handleSelect}
                    initialState={{
                        filterModel: { status: { value: 'referral intake pending admitted' } },
                    }}
                />
            </Box>
        </Flex>
    );
}
